import styled, { css } from 'styled-components';
import NextImage from 'next/image';
import { ReactComponent as mapSkyscraper } from 'Public/images/accommodation-map/skyscraper.svg';
import { ReactComponent as WaveSVG } from 'Public/graphics/waves/accommodation/wave-map.svg';

import scrollbarStyle from 'Common/styles/scrollbar';

import Section from 'Elements/Section';
import Link from 'Elements/Link';
import Select from 'Elements/Input/Select';

export const StyledSection = styled(Section)<{ $listingHeight: number }>`
    ${({ theme, $listingHeight }) => css`
        position: relative;
        overflow: hidden;

        &:before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(
                to top,
                ${theme.colors.secondary.tints[1].hex}
                    calc(${$listingHeight / 2 / 16}rem + var(--default-padding)),
                transparent ${$listingHeight / 2 / 16}rem
            );
            z-index: 1;
            pointer-events: none;
        }
    `}
`;

export const Description = styled.p`
    margin: 0;
`;

export const StyledLink = styled(Link)`
    ${({ theme }) => css`
        position: absolute;
        left: 0;
        bottom: -2rem;

        @media ${theme.breakpoints.large.media} {
            position: relative;
            left: auto;
            bottom: auto;
        }
    `}
`;

export const WidgetWrapper = styled.div`
    margin-top: 3rem;
    position: relative;
    z-index: 1;
`;

export const MapWrapper = styled.div`
    position: relative;
`;

export const StyledMap = styled(NextImage)`
    ${({ theme }) => css`
        display: block;
        border-radius: 0.5rem;
        max-width: 100%;
        height: auto;

        @media ${theme.breakpoints.large.media} {
            border-radius: 0.5rem 0 0 0.5rem;
        }
    `}
`;

export const LogoWrapper = styled.div`
    ${({ theme }) => css`
        position: absolute;
        left: 19%;
        bottom: 60%;
        width: 2rem;
        height: 2rem;

        @media ${theme.breakpoints.large.media} {
            width: 2.5rem;
            height: 2.5rem;
        }
    `}
`;

export const MapButton = styled.button<{ $active: boolean; $colour: string }>`
    ${({ theme, $active, $colour }) => css`
        position: absolute;
        width: 1.75rem;
        height: 1.75rem;
        padding: 0;
        background: ${theme.colors.tertiary.hex};
        border-radius: 50%;
        border: ${theme.commonStyles.brandPixel} solid ${theme.colors.tertiaryContrast.hex};
        display: flex;
        justify-content: center;
        align-items: center;
        transform: translate(-50%, 50%);
        transform-style: preserve-3d;
        cursor: pointer;

        &:before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: ${theme.colors.tertiaryContrast.hex};
            border-radius: 50%;
            opacity: 0.7;
            transform: translateZ(-${theme.commonStyles.brandPixel});
            transition-duration: 0.2s;
        }

        ${StyledIcon} path {
            stroke: ${$colour};
        }

        &:hover,
        &:focus {
            background: ${$colour};
            border-color: transparent;
            outline: none;

            &:before {
                transform-origin: center;
                transform: translateZ(-${theme.commonStyles.brandPixel}) scale(1.25);
            }

            ${StyledIcon} path {
                stroke: ${theme.colors.tertiary.hex};
            }
        }

        ${$active &&
        css`
            background: ${$colour};
            border-color: transparent;
            outline: 0.25rem solid ${theme.colors.tertiary.hex};
            pointer-events: none;

            &:focus {
                background: ${$colour};
                border-color: transparent;
                outline: 0.25rem solid ${theme.colors.tertiary.hex};
                pointer-events: none;
            }

            ${StyledIcon} path {
                stroke: ${theme.colors.tertiary.hex};
            }
        `}

        @media ${theme.breakpoints.mediumSmall.media} {
            width: 2.5rem;
            height: 2.5rem;
        }

        @media ${theme.breakpoints.large.media} {
            width: 2rem;
            height: 2rem;
        }

        @media ${theme.breakpoints.xlarge.media} {
            width: 2.5rem;
            height: 2.5rem;
        }
    `}
`;

export const StyledIcon = styled(mapSkyscraper)`
    width: 50%;
    height: auto;
    position: relative;
    left: 0.03rem;
`;

export const FilterWrapper = styled.div`
    ${({ theme }) => css`
        display: none;

        @media ${theme.breakpoints.large.media} {
            background: ${theme.colors.tertiary.hex};
            border-radius: 0 0.5rem 0 0;
            padding: 0.5rem 1.7rem 0.5rem 0.5rem;
            height: 3.25rem;
            display: flex;
            align-items: center;
            justify-content: flex-end;
        }
    `}
`;

export const Label = styled.label`
    ${({ theme }) => css`
        font-weight: 400;
        margin-right: 0.75rem;
        color: ${theme.colors.tertiaryContrast.hex};
    `}
`;

export const StyledSelect = styled(Select)`
    width: 15rem;
`;

export const ListingWrapper = styled.ul`
    ${({ theme }) => css`
        position: relative;
        margin: 0;
        padding: 0 1.25rem;
        list-style: none;
        background: ${theme.colors.secondary.tints[0].hex};
        border-radius: 0 0 0.5rem 0.5rem;
        display: flex;
        overflow-x: auto;
        width: calc(100% + 1.5rem);
        left: -0.75rem;

        ${scrollbarStyle()}
        ::-webkit-scrollbar-track {
            background: ${theme.colors.background.hex};
        }

        ::-webkit-scrollbar-thumb {
            background: ${theme.colors.secondary.tints[5].hex};
        }

        @media ${theme.breakpoints.large.media} {
            display: block;
            padding: 0 1.25rem 1.25rem;
            border-radius: 0 0 0.5rem 0;
            overflow-y: auto;
            width: auto;
            left: auto;

            ::-webkit-scrollbar-track {
                border-radius: 0 0 0.5rem 0;
            }
        }
    `}
`;

export const Wave = styled(WaveSVG)`
    ${({ theme }) => css`
        display: none;

        @media ${theme.breakpoints.large.media} {
            display: block;
            position: absolute;
            z-index: 0;
            pointer-events: none;
            left: -28rem;
            top: 11rem;
            width: 163rem;
            height: 47.5rem;
        }
    `}
`;
